import { useState, useEffect } from 'react';
import { getApiUrl } from '../config/api';
import type { Brand, PrinterModel, Vertical } from '../types';

type ModalMode = 'create' | 'edit';

type ExtrasConfig = Record<string, string>;

interface ExtraField {
  key: string;
  label: string;
  defaultValue: string;
}

const EXTRAS_BY_VERTICAL: Record<string, ExtraField[]> = {
  copiers: [
    { key: 'extra_stapler',    label: 'Stapler Finisher (£/month)',               defaultValue: '4.99'  },
    { key: 'extra_booklet',    label: 'Booklet Maker (£/month)',                   defaultValue: '9.99'  },
    { key: 'extra_hole_punch', label: 'Hole Puncher (£/month)',                    defaultValue: '7.99'  },
    { key: 'extra_large_tray', label: 'Large Capacity Tray (£/month)',             defaultValue: '15.00' },
  ],
  printers: [
    { key: 'extra_paper_tray', label: 'Extra Paper Tray (£/month)',                defaultValue: '4.99'  },
    { key: 'extra_wireless',   label: 'Wireless Printing (£/month)',               defaultValue: '2.99'  },
  ],
};

// Deduplicated list of every configurable extra across verticals. Extras
// pricing is stored as global config, so we load and persist all keys
// regardless of which vertical is currently being viewed.
const ALL_EXTRAS: ExtraField[] = Array.from(
  new Map(Object.values(EXTRAS_BY_VERTICAL).flat().map(f => [f.key, f])).values()
);

const COPIER_FORMATS = ['A4', 'A3'];
const PRINTER_FORMATS = ['A4 Mono', 'A4 Colour'];

const FORMATS_BY_VERTICAL: Record<string, string[]> = {
  copiers: COPIER_FORMATS,
  printers: PRINTER_FORMATS,
};

const FORMAT_LABELS: Record<string, string> = {
  'A4': 'A4',
  'A3': 'A3',
  'A4 Mono': 'A4 Mono',
  'A4 Colour': 'A4 Colour',
};

const EMPTY_MODEL: Omit<PrinterModel, 'id' | 'created_at' | 'updated_at'> = {
  brand_id: null,
  vertical: 'copiers',
  name: '',
  description: '',
  format: 'A4',
  min_mono_volume: 0,
  max_mono_volume: null,
  min_colour_volume: 0,
  max_colour_volume: null,
  base_lease: 29.99,
  mono_cost_per_copy: 0.005,
  colour_cost_per_copy: 0.045,
  allow_stapler: 0,
  allow_booklet_maker: 0,
  allow_hole_punch: 0,
  active: 1,
  display_order: 0,
};

interface PrinterModelsPageProps {
  defaultVertical?: Vertical;
}

export function PrinterModelsPage({ defaultVertical }: PrinterModelsPageProps) {
  const [models, setModels] = useState<PrinterModel[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [filterVertical, setFilterVertical] = useState<string>(defaultVertical ?? '');
  const [filterFormat, setFilterFormat] = useState<string>('');
  const [filterBrand, setFilterBrand] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Extras pricing state — holds every extra key (global config); the form
  // below shows the subset relevant to the selected vertical.
  const [extras, setExtras] = useState<ExtrasConfig>(
    Object.fromEntries(ALL_EXTRAS.map(f => [f.key, f.defaultValue]))
  );
  const [savingExtras, setSavingExtras] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>('create');
  const [editingModel, setEditingModel] = useState<Partial<PrinterModel>>(EMPTY_MODEL);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchBrands();
    fetchModels();
    fetchExtras();
  }, []);

  useEffect(() => {
    fetchModels();
  }, [filterFormat, filterBrand, filterVertical]);

  const fetchBrands = async () => {
    try {
      const response = await fetch(getApiUrl('/api/brands'), {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setBrands(data.brands || []);
      }
    } catch (err) {
      console.error('Failed to fetch brands:', err);
    }
  };

  const fetchModels = async () => {
    try {
      setLoading(true);
      setError('');

      let url = '/api/pricing/models';
      const params = new URLSearchParams();
      if (filterVertical) params.set('vertical', filterVertical);
      if (filterFormat) params.set('format', filterFormat);
      if (filterBrand) params.set('brand', filterBrand);
      const qs = params.toString();
      if (qs) url += `?${qs}`;

      const response = await fetch(getApiUrl(url), {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setModels(data.models || []);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.error || 'Failed to load printer models');
      }
    } catch (err) {
      console.error('Failed to fetch models:', err);
      setError('Failed to load printer models');
    } finally {
      setLoading(false);
    }
  };

  const fetchExtras = async () => {
    try {
      const response = await fetch(getApiUrl('/api/pricing/config'), {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        if (data.config && data.config.length > 0) {
          const configObj: Record<string, string> = {};
          for (const item of data.config as { name: string; value: string }[]) {
            configObj[item.name] = item.value;
          }
          const newExtras: ExtrasConfig = Object.fromEntries(
            ALL_EXTRAS.map(f => [f.key, configObj[f.key] ?? f.defaultValue])
          );
          setExtras(newExtras);
        }
      }
    } catch (err) {
      console.error('Failed to fetch extras config:', err);
    }
  };

  const handleSaveExtras = async () => {
    setSavingExtras(true);
    setError('');
    try {
      const response = await fetch(getApiUrl('/api/pricing/config'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(extras),
      });
      if (response.ok) {
        setSuccess('Extra features pricing saved successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.error || 'Failed to save extras pricing');
      }
    } catch (err) {
      console.error('Save extras error:', err);
      setError('Failed to save extras pricing');
    } finally {
      setSavingExtras(false);
    }
  };

  const updateExtra = (key: keyof ExtrasConfig, value: string) => {
    setExtras(prev => ({ ...prev, [key]: value }));
  };

  const openCreateModal = () => {
    setEditingModel({ ...EMPTY_MODEL });
    setModalMode('create');
    setShowModal(true);
    setError('');
  };

  const openEditModal = (model: PrinterModel) => {
    setEditingModel({ ...model });
    setModalMode('edit');
    setShowModal(true);
    setError('');
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingModel(EMPTY_MODEL);
    setError('');
  };

  const handleSave = async () => {
    if (!editingModel.name?.trim()) {
      setError('Model name is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const isEdit = modalMode === 'edit' && editingModel.id;
      const url = isEdit
        ? getApiUrl(`/api/pricing/models/${editingModel.id}`)
        : getApiUrl('/api/pricing/models');

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          brand_id: editingModel.brand_id || null,
          vertical: editingModel.vertical || 'copiers',
          name: editingModel.name,
          description: editingModel.description || null,
          format: editingModel.format,
          min_mono_volume: Number(editingModel.min_mono_volume) || 0,
          max_mono_volume: editingModel.max_mono_volume ? Number(editingModel.max_mono_volume) : null,
          min_colour_volume: Number(editingModel.min_colour_volume) || 0,
          max_colour_volume: editingModel.max_colour_volume ? Number(editingModel.max_colour_volume) : null,
          base_lease: Number(editingModel.base_lease),
          mono_cost_per_copy: Number(editingModel.mono_cost_per_copy),
          colour_cost_per_copy: Number(editingModel.colour_cost_per_copy),
          allow_stapler: editingModel.allow_stapler ? true : false,
          allow_booklet_maker: editingModel.allow_booklet_maker ? true : false,
          allow_hole_punch: editingModel.allow_hole_punch ? true : false,
          active: editingModel.active ? true : false,
          display_order: Number(editingModel.display_order) || 0,
        }),
      });

      if (response.ok) {
        setSuccess(isEdit ? 'Printer model updated successfully' : 'Printer model created successfully');
        setTimeout(() => setSuccess(''), 3000);
        closeModal();
        fetchModels();
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.error || 'Failed to save product model');
      }
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save product model');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (model: PrinterModel) => {
    if (!confirm(`Deactivate "${model.name}"? It will no longer be used for pricing calculations.`)) {
      return;
    }

    try {
      const response = await fetch(getApiUrl(`/api/pricing/models/${model.id}`), {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setSuccess(`"${model.name}" deactivated successfully`);
        setTimeout(() => setSuccess(''), 3000);
        fetchModels();
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.error || 'Failed to deactivate model');
      }
    } catch (err) {
      console.error('Deactivate error:', err);
      setError('Failed to deactivate model');
    }
  };

  const updateField = (field: string, value: any) => {
    setEditingModel(prev => ({ ...prev, [field]: value }));
  };

  const getBrandName = (brandId: string | null) => {
    if (!brandId) return 'All Brands';
    const brand = brands.find(b => b.id === brandId);
    return brand ? brand.name : brandId;
  };

  const formatVolume = (min: number, max: number | null) => {
    if (max === null || max === undefined) return `${min.toLocaleString()}+`;
    return `${min.toLocaleString()} - ${max.toLocaleString()}`;
  };

  if (loading && models.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-dark-text-secondary">Loading printer models...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">
            Printer Settings
          </h1>
          <p className="text-dark-text-secondary text-sm sm:text-base">
            Manage pricing model tiers and rates. These are used by the calculator for pricing estimates.
          </p>
        </div>
        <button onClick={openCreateModal} className="btn btn-primary whitespace-nowrap w-full sm:w-auto">
          + Add Model
        </button>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-6 p-4 bg-green-900/20 border border-green-500 rounded-lg text-green-400">
          {success}
        </div>
      )}

      {/* Error Message */}
      {error && !showModal && (
        <div className="mb-6 p-4 bg-red-900/20 border border-red-500 rounded-lg text-red-400">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="card p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div>
          <label className="block text-xs font-medium text-dark-text-secondary mb-1">Vertical</label>
          <select
            value={filterVertical}
            onChange={(e) => {
              setFilterVertical(e.target.value);
              setFilterFormat('');
            }}
            className="input py-1.5 text-sm"
          >
            <option value="">All Verticals</option>
            <option value="copiers">Copiers</option>
            <option value="printers">Printers</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-dark-text-secondary mb-1">Format</label>
          <select
            value={filterFormat}
            onChange={(e) => setFilterFormat(e.target.value)}
            className="input py-1.5 text-sm"
          >
            <option value="">All Formats</option>
            {(filterVertical ? FORMATS_BY_VERTICAL[filterVertical] ?? COPIER_FORMATS : [...COPIER_FORMATS, ...PRINTER_FORMATS]).map(f => (
              <option key={f} value={f}>{FORMAT_LABELS[f] || f}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-dark-text-secondary mb-1">Brand</label>
          <select
            value={filterBrand}
            onChange={(e) => setFilterBrand(e.target.value)}
            className="input py-1.5 text-sm"
          >
            <option value="">All Brands</option>
            {brands.map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:ml-auto text-sm text-dark-text-secondary">
          {models.length} model{models.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Extra Features Pricing */}
      <div className="card p-4 sm:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-bold text-white">Extra Features Pricing</h2>
            <p className="text-sm text-dark-text-secondary">
              Monthly add-on costs for optional extras. These apply globally when a user selects extras in the calculator. Use the Vertical filter above to switch between copier and printer extras.
            </p>
          </div>
          <button
            onClick={handleSaveExtras}
            disabled={savingExtras}
            className="btn btn-primary whitespace-nowrap text-sm w-full sm:w-auto"
          >
            {savingExtras ? 'Saving...' : 'Save Extras'}
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {(filterVertical ? EXTRAS_BY_VERTICAL[filterVertical] ?? ALL_EXTRAS : ALL_EXTRAS).map((field) => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-dark-text mb-1">{field.label}</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={extras[field.key] ?? field.defaultValue}
                onChange={(e) => updateExtra(field.key, e.target.value)}
                className="input w-full"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Models Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dark-border">
                <th className="text-left p-4 text-sm font-medium text-dark-text-secondary">Model</th>
                <th className="text-left p-4 text-sm font-medium text-dark-text-secondary">Format</th>
                <th className="text-left p-4 text-sm font-medium text-dark-text-secondary">Brand</th>
                <th className="text-left p-4 text-sm font-medium text-dark-text-secondary">B&W Volume</th>
                <th className="text-left p-4 text-sm font-medium text-dark-text-secondary">Colour Volume</th>
                <th className="text-right p-4 text-sm font-medium text-dark-text-secondary">Base Lease</th>
                <th className="text-right p-4 text-sm font-medium text-dark-text-secondary">B&W Cost/Page</th>
                <th className="text-right p-4 text-sm font-medium text-dark-text-secondary">Colour Cost/Page</th>
                <th className="text-left p-4 text-sm font-medium text-dark-text-secondary">Finishing</th>
                <th className="text-right p-4 text-sm font-medium text-dark-text-secondary">Actions</th>
              </tr>
            </thead>
            <tbody>
              {models.length === 0 ? (
                <tr>
                  <td colSpan={10} className="p-8 text-center text-dark-text-secondary">
                    No printer models found. Click "Add Model" to create one.
                  </td>
                </tr>
              ) : (
                models.map((model) => (
                  <tr
                    key={model.id}
                    className="border-b border-dark-border hover:bg-slate-800/50 transition-colors"
                  >
                    <td className="p-4">
                      <div>
                        <p className="font-medium text-dark-text">{model.name}</p>
                        {model.description && (
                          <p className="text-xs text-dark-text-secondary mt-0.5">{model.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        model.format === 'A3'
                          ? 'bg-purple-900/30 text-purple-400 border border-purple-700'
                          : 'bg-blue-900/30 text-blue-400 border border-blue-700'
                      }`}>
                        {FORMAT_LABELS[model.format] || model.format}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-dark-text-secondary">
                      {getBrandName(model.brand_id)}
                    </td>
                    <td className="p-4 text-sm text-dark-text">
                      {formatVolume(model.min_mono_volume, model.max_mono_volume)}
                    </td>
                    <td className="p-4 text-sm text-dark-text">
                      {formatVolume(model.min_colour_volume, model.max_colour_volume)}
                    </td>
                    <td className="p-4 text-right text-sm font-mono text-dark-text">
                      £{model.base_lease.toFixed(2)}
                    </td>
                    <td className="p-4 text-right text-sm font-mono text-dark-text">
                      £{model.mono_cost_per_copy.toFixed(4)}
                    </td>
                    <td className="p-4 text-right text-sm font-mono text-dark-text">
                      £{model.colour_cost_per_copy.toFixed(4)}
                    </td>
                    <td className="p-4">
                      <div className="flex gap-1.5">
                        {model.allow_stapler ? (
                          <span className="text-xs bg-slate-700 text-dark-text-secondary px-1.5 py-0.5 rounded" title="Stapler">S</span>
                        ) : null}
                        {model.allow_booklet_maker ? (
                          <span className="text-xs bg-slate-700 text-dark-text-secondary px-1.5 py-0.5 rounded" title="Booklet">B</span>
                        ) : null}
                        {model.allow_hole_punch ? (
                          <span className="text-xs bg-slate-700 text-dark-text-secondary px-1.5 py-0.5 rounded" title="Hole Punch">H</span>
                        ) : null}
                        {!model.allow_stapler && !model.allow_booklet_maker && !model.allow_hole_punch && (
                          <span className="text-xs text-dark-text-secondary">None</span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(model)}
                          className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeactivate(model)}
                          className="text-sm text-red-400 hover:text-red-300 transition-colors"
                        >
                          Deactivate
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-dark-surface border border-dark-border rounded-xl w-full max-w-2xl my-2 sm:my-0">
            <div className="p-6 border-b border-dark-border flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {modalMode === 'create' ? 'Add Product Model' : 'Edit Product Model'}
              </h2>
              <button onClick={closeModal} className="text-dark-text-secondary hover:text-white text-2xl leading-none">
                &times;
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Error in modal */}
              {error && (
                <div className="p-3 bg-red-900/20 border border-red-500 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}

              {/* Basic Info */}
              <div>
                <h3 className="text-sm font-semibold text-dark-text-secondary uppercase tracking-wider mb-3">Basic Info</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-dark-text mb-1">Name *</label>
                    <input
                      type="text"
                      value={editingModel.name || ''}
                      onChange={(e) => updateField('name', e.target.value)}
                      className="input w-full"
                      placeholder="e.g. A4 Professional"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark-text mb-1">Vertical *</label>
                    <select
                      value={editingModel.vertical || 'copiers'}
                      onChange={(e) => {
                        const v = e.target.value as Vertical;
                        updateField('vertical', v);
                        // Reset format to a valid option for the new vertical
                        const formats = FORMATS_BY_VERTICAL[v] ?? COPIER_FORMATS;
                        if (!editingModel.format || !formats.includes(editingModel.format)) {
                          updateField('format', formats[0]);
                        }
                      }}
                      className="input w-full"
                    >
                      <option value="copiers">Copiers</option>
                      <option value="printers">Printers</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark-text mb-1">Format / Type *</label>
                    <select
                      value={editingModel.format || 'A4'}
                      onChange={(e) => updateField('format', e.target.value)}
                      className="input w-full"
                    >
                      {(FORMATS_BY_VERTICAL[editingModel.vertical ?? 'copiers'] ?? COPIER_FORMATS).map(f => (
                        <option key={f} value={f}>{FORMAT_LABELS[f] || f}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-dark-text mb-1">Description</label>
                    <input
                      type="text"
                      value={editingModel.description || ''}
                      onChange={(e) => updateField('description', e.target.value)}
                      className="input w-full"
                      placeholder="Optional description"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark-text mb-1">Brand</label>
                    <select
                      value={editingModel.brand_id || ''}
                      onChange={(e) => updateField('brand_id', e.target.value || null)}
                      className="input w-full"
                    >
                      <option value="">All Brands (Global)</option>
                      {brands.map((brand) => (
                        <option key={brand.id} value={brand.id}>
                          {brand.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark-text mb-1">Display Order</label>
                    <input
                      type="number"
                      value={editingModel.display_order ?? 0}
                      onChange={(e) => updateField('display_order', parseInt(e.target.value) || 0)}
                      className="input w-full"
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Cost Per Copy / Page */}
              <div>
                <h3 className="text-sm font-semibold text-dark-text-secondary uppercase tracking-wider mb-3">
                  Cost Per Page
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-dark-text mb-1">Base Lease (£/month) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingModel.base_lease ?? ''}
                      onChange={(e) => updateField('base_lease', parseFloat(e.target.value) || 0)}
                      className="input w-full"
                    />
                    <p className="text-xs text-dark-text-secondary mt-1">Monthly lease cost</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark-text mb-1">
                      B&W Cost/Page (£) *
                    </label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      value={editingModel.mono_cost_per_copy ?? ''}
                      onChange={(e) => updateField('mono_cost_per_copy', parseFloat(e.target.value) || 0)}
                      className="input w-full"
                    />
                    <p className="text-xs text-dark-text-secondary mt-1">
                      Typical: £0.003 - £0.007
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark-text mb-1">
                      Colour Cost/Page (£) *
                    </label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      value={editingModel.colour_cost_per_copy ?? ''}
                      onChange={(e) => updateField('colour_cost_per_copy', parseFloat(e.target.value) || 0)}
                      className="input w-full"
                    />
                    <p className="text-xs text-dark-text-secondary mt-1">
                      Typical: £0.04 - £0.06
                    </p>
                  </div>
                </div>
              </div>

              {/* Volume Ranges */}
              <div>
                <h3 className="text-sm font-semibold text-dark-text-secondary uppercase tracking-wider mb-3">
                  Volume Ranges (pages/month)
                </h3>
                <p className="text-xs text-dark-text-secondary mb-3">
                  Defines which volume range this model tier applies to. Leave max blank for unlimited.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-dark-text mb-1">
                      Min B&W Volume
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={editingModel.min_mono_volume ?? 0}
                      onChange={(e) => updateField('min_mono_volume', parseInt(e.target.value) || 0)}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark-text mb-1">
                      Max B&W Volume
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={editingModel.max_mono_volume ?? ''}
                      onChange={(e) => updateField('max_mono_volume', e.target.value ? parseInt(e.target.value) : null)}
                      className="input w-full"
                      placeholder="Unlimited"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark-text mb-1">
                      Min Colour Volume
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={editingModel.min_colour_volume ?? 0}
                      onChange={(e) => updateField('min_colour_volume', parseInt(e.target.value) || 0)}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark-text mb-1">
                      Max Colour Volume
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={editingModel.max_colour_volume ?? ''}
                      onChange={(e) => updateField('max_colour_volume', e.target.value ? parseInt(e.target.value) : null)}
                      className="input w-full"
                      placeholder="Unlimited"
                    />
                  </div>
                </div>
              </div>

              {/* Finishing Options - copiers only */}
              {editingModel.vertical === 'copiers' && <div>
                <h3 className="text-sm font-semibold text-dark-text-secondary uppercase tracking-wider mb-3">Allowed Finishing Options</h3>
                <p className="text-xs text-dark-text-secondary mb-3">
                  Select which finishing options are available for this model tier. A4 models typically have none.
                </p>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!editingModel.allow_stapler}
                      onChange={(e) => updateField('allow_stapler', e.target.checked ? 1 : 0)}
                      className="w-4 h-4 rounded border-gray-600 bg-slate-700 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-dark-text">Stapler Finisher</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!editingModel.allow_booklet_maker}
                      onChange={(e) => updateField('allow_booklet_maker', e.target.checked ? 1 : 0)}
                      className="w-4 h-4 rounded border-gray-600 bg-slate-700 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-dark-text">Booklet Maker</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!editingModel.allow_hole_punch}
                      onChange={(e) => updateField('allow_hole_punch', e.target.checked ? 1 : 0)}
                      className="w-4 h-4 rounded border-gray-600 bg-slate-700 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-dark-text">Hole Puncher</span>
                  </label>
                </div>
              </div>}

              {/* Active Status */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!editingModel.active}
                    onChange={(e) => updateField('active', e.target.checked ? 1 : 0)}
                    className="w-4 h-4 rounded border-gray-600 bg-slate-700 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-dark-text">Active</span>
                  <span className="text-xs text-dark-text-secondary">- Inactive models won't be used for pricing calculations</span>
                </label>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-dark-border flex items-center justify-end gap-3">
              <button onClick={closeModal} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn btn-primary"
              >
                {saving
                  ? 'Saving...'
                  : modalMode === 'create'
                    ? 'Create Model'
                    : 'Save Changes'
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
